import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc, addDoc, updateDoc, collection } from "firebase/firestore";
import { db } from "../../firebase";
import Select from "react-select";
import { selectStyles } from "../Constants";
import { DEFAULT_INVOICE_DATA, DEFAULT_CHARGES_DATA } from "../Constants";
import PdfPreviewModal from "../PreviewPdfModal";
import { toast } from "react-toastify";
import Loader from "../../utils/Loader";
import { useInvoiceContext } from "../../contexts/InvoiceContext";
import { useUser } from "../../contexts/UserContext";
import { useAdminContext } from "../../contexts/AdminContext";
import SendInvoiceModal from "./SendInvoiceModal";
import ConfirmNavigationModal from "../ConfirmNavigationModal";
import {
  calculateOfferExpiry,
  calculateInvoiceTotals,
  formatCurrency,
  savePdfToCloud,
  mailForm,
  formatInvoiceNumber,
  getLastInvoiceNumber,
  updateLastInvoiceNumber,
  findExistingClient,
  generateNextCID,
  hasClientDataChanged,
  encryptInvoice,
} from "../../utils/Utility";
import { formatOldUrl, decryptData } from "../../utils/cloudApi";
import { generatePDF } from "./GenerateInvoice";

const InvoiceForm = ({ setNavbarButtons }) => {
  const [formType, setFormType] = useState("inv");
  const defaultData =
    formType === "noc" ? DEFAULT_CHARGES_DATA : DEFAULT_INVOICE_DATA;
  const defaultType = formType === "noc" ? "Notice of Charges" : "Invoice";
  const {
    user,
    invoiceClients,
    addInvoiceClient,
    updateInvoiceClient,
    privateKeyInMemory,
    getRecipients,
    getRecipientById,
  } = useUser();
  const {
    isViewingOtherUser,
    getSelectedUserName,
    adminData,
    selectedUserId,
    selectedUserData,
    addUserInvoiceClient,
    updateUserInvoiceClient,
    addUserInvoice,
    updateUserInvoice,
  } = useAdminContext();

  const { setInvoices, invoices, templates } = useInvoiceContext();
  // Dynamic data selection based on admin mode
  const currentUser = isViewingOtherUser ? selectedUserData : user;
  const currentInvoiceClients = isViewingOtherUser
    ? adminData?.userClients
    : invoiceClients;
  const currentAddClient = isViewingOtherUser
    ? addUserInvoiceClient
    : addInvoiceClient;
  const currentUpdateClient = isViewingOtherUser
    ? updateUserInvoiceClient
    : updateInvoiceClient;
  const currentInvoices = isViewingOtherUser
    ? adminData?.userInvoices
    : invoices;
  const currentTemplates = isViewingOtherUser
    ? adminData?.userTemplates
    : templates;

  const location = useLocation();

  const navigate = useNavigate();
  const template = location.state?.template;
  const [showPreview, setShowPreview] = useState(false);
  const [localPdfUrl, setLocalPdfUrl] = useState("");
  const { id } = useParams();
  const isNew = !id;
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [header, setHeader] = useState(defaultData.header);
  const [formData, setFormData] = useState(defaultData);
  const [originalData, setOriginalData] = useState(defaultData);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [showVatOptions, setShowVatOptions] = useState(false);
  const [isInstallment, setIsInstallment] = useState(false);
  const [installmentMonths, setInstallmentMonths] = useState(2);
  const [installmentPayments, setInstallmentPayments] = useState([]);

  const vatOptions = [
    { value: "tax", label: "Tax" },
    { value: "non-vat", label: "Non-VAT" },
    { value: "vat", label: "VAT" },
  ];

  const vatMethodOptions = [
    {
      value: "inclusive",
      label: formData?.vatOption === "tax" ? "Tax Inclusive" : "VAT Inclusive",
    },
    {
      value: "exclusive",
      label: formData?.vatOption === "tax" ? "Tax Exclusive" : "VAT Exclusive",
    },
  ];

  // Helper function to add months to a date
  const addMonths = (dateString, months) => {
    const date = new Date(dateString);
    date.setMonth(date.getMonth() + months);
    return date.toISOString().split("T")[0];
  };

  useEffect(() => {
    const loadTemplate = async () => {
      if (template && isNew) {
        const decrypted = await decryptData(
          template,
          user.uid,
          privateKeyInMemory
        );

        const initialItems =
          decrypted.items && decrypted.items.length
            ? decrypted.items.map((item) => ({
                description: item.description ?? "",
                quantity: item.quantity ?? 1,
                unitCost: item.unitCost ?? 0,
                discount: item.discount ?? 0,
                comment: item.comment ?? "",
                subTotal: item.subTotal ?? 0,
              }))
            : [
                {
                  description: "",
                  quantity: 1,
                  unitCost: 0,
                  discount: 0,
                  comment: "",
                  subTotal: 0,
                },
              ];

        // find the template in store and get the "tempType"
        const tempId = decrypted.id ? decrypted.id : decrypted.templateRef;
        const temp =
          tempId && currentTemplates
            ? currentTemplates.find((tpl) => tpl.id === tempId)
            : null;
        const tempType = temp?.tempType ? temp?.tempType : "inv";

        setFormType(tempType);

        const invoiceNumber = Number(decrypted.lastInvoiceNumber ?? "");
        const formattedInvoiceNo = formatInvoiceNumber(invoiceNumber, tempType);
        const currentDate = defaultData.date;

        const loadedData = {
          header: decrypted.header,
          headerDescription: decrypted.headerDescription || "",
          invoiceNo: formattedInvoiceNo,
          logo: decrypted.logo || null,
          date: currentDate,
          offerExpiry:
            decrypted.offerExpiry || calculateOfferExpiry(currentDate),
          from: { ...decrypted.from },
          to: { ...defaultData.to, ...decrypted.to },
          items: initialItems,
          pdfUrl: decrypted.pdfUrl || null,
          templateRef: decrypted.id ? decrypted.id : decrypted.templateRef,
          vatOption: decrypted.vatOption || "vat",
          vatMethod: decrypted.vatMethod || "inclusive",
          taxCode: decrypted.taxCode || "",
          vatRate: decrypted.vatRate || 12,
          isInstallment: decryptData.isInstallment || false,
          installmentMonths: decryptData.installmentMonths || 2,
        };
        setHeader(decrypted.header);
        setFormData(loadedData);

        if (decrypted.isInstallment) {
          setIsInstallment(true);
          setInstallmentMonths(decrypted.installmentMonths || 2);
          if (decrypted.installmentPayments) {
            setInstallmentPayments(decrypted.installmentPayments);
          }
        }

        setOriginalData(JSON.parse(JSON.stringify(loadedData)));
      }
    };

    loadTemplate();
  }, [
    template,
    isNew,
    currentTemplates,
    defaultData,
    privateKeyInMemory,
    user,
  ]);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  useEffect(() => {
    formData.items.forEach((_, index) => {
      const descriptionTextArea = document.getElementById(
        `description-${index}`
      );
      if (descriptionTextArea) {
        descriptionTextArea.style.height = "auto";
        descriptionTextArea.style.height = `${descriptionTextArea.scrollHeight}px`;
      }
      const commentTextArea = document.getElementById(`comment-${index}`);
      if (commentTextArea) {
        commentTextArea.style.height = "auto";
        commentTextArea.style.height = `${commentTextArea.scrollHeight}px`;
      }
    });
  }, [formData]);

  useEffect(() => {
    if (!isNew) {
      const invoiceFromState = currentInvoices.find((inv) => inv.id === id);
      if (invoiceFromState) {
        const loadInvoice = async () => {
          try {
            const decrypted = await decryptData(
              invoiceFromState,
              user.uid,
              privateKeyInMemory
            );

            const detectedFormType = decrypted.invoiceNo?.startsWith("NC")
              ? "noc"
              : "inv";
            setFormType(detectedFormType);

            const loadedData = {
              ...decrypted,
              headerColor: decrypted.headerColor || [0, 0, 0],
              tableHeaderTextColor: decrypted.tableHeaderTextColor || [
                255, 255, 255,
              ],
              tableHeaderColor: decrypted.tableHeaderColor || [43, 112, 115],
              vatOption: decrypted.vatOption || "vat",
              vatMethod: decrypted.vatMethod || "inclusive",
              taxCode: decrypted.taxCode || "",
              vatRate: decrypted.vatRate || 12,
              offerExpiry:
                decrypted.offerExpiry || calculateOfferExpiry(decrypted.date),
            };

            setIsInstallment(loadedData.isInstallment || false);
            setInstallmentMonths(loadedData.installmentMonths || 2);
            if (loadedData.installmentPayments) {
              setInstallmentPayments(loadedData.installmentPayments);
            }
            setHeader(loadedData.header);
            setFormData(loadedData);
            setOriginalData(loadedData);
          } catch (error) {
            console.error("Error decrypting invoice:", error);
            toast.error("Failed to decrypt invoice");
          }
        };

        loadInvoice();
      } else {
        // Fallback: fetch from Firebase
        const fetchInvoice = async () => {
          try {
            const docRef = doc(db, "invoices", id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
              const data = docSnap.data();

              const decrypted = await decryptData(
                data,
                user.uid,
                privateKeyInMemory
              );

              const detectedFormType = decrypted.invoiceNo?.startsWith("NC")
                ? "noc"
                : "inv";
              setFormType(detectedFormType);

              const loadedData = {
                ...decrypted,
                headerColor: decrypted.headerColor || [0, 0, 0],
                tableHeaderColor: decrypted.tableHeaderColor || [43, 112, 115],
                tableHeaderTextColor: decrypted.tableHeaderTextColor || [
                  255, 255, 255,
                ],
                vatOption: decrypted.vatOption || "vat",
                vatMethod: decrypted.vatMethod || "inclusive",
                taxCode: decrypted.taxCode || "inclusive",
                vatRate: decrypted.vatRate || 12,
                offerExpiry:
                  decrypted.offerExpiry || calculateOfferExpiry(decrypted.date),
              };

              setHeader(loadedData.header);
              setFormData(loadedData);
              setOriginalData(loadedData);
            } else {
              console.error(`${defaultType} not found!`);
              toast.error(`${defaultType} not found!`);
            }
          } catch (error) {
            console.error("Error fetching/decrypting invoice:", error);
            toast.error("Failed to load invoice");
          }
        };

        fetchInvoice();
      }
    }
  }, [id, isNew, currentInvoices, defaultType, user.uid, privateKeyInMemory]);

  // Calculate installment payments whenever relevant values change
  useEffect(() => {
    if (!isInstallment) {
      setInstallmentPayments([]);
      return;
    }

    const totals = calculateInvoiceTotals(formData.items || [], {
      vatOption: formData.vatOption,
      vatMethod: formData.vatMethod,
      vatRate: formData.vatRate,
    });

    const totalAmount = totals.totalPayable;
    const startDate = formData.date || new Date().toISOString().split("T")[0];

    let newPayments = [];

    if (installmentMonths === 2) {
      // For 2 months: split equally
      const monthlyPayment = totalAmount / 2;
      newPayments = [
        {
          month: 1,
          amount: monthlyPayment,
          label: "Downpayment",
          date: startDate,
        },
        {
          month: 2,
          amount: monthlyPayment,
          label: "Final Payment",
          date: addMonths(startDate, 1),
        },
      ];
    } else {
      // For 3+ months: 50% downpayment, remaining split equally
      const downpayment = totalAmount * 0.5;
      const remaining = totalAmount - downpayment;
      const monthlyPayment = remaining / (installmentMonths - 1);

      newPayments = [
        {
          month: 1,
          amount: downpayment,
          label: "Downpayment (50%)",
          date: startDate,
        },
      ];

      for (let i = 2; i <= installmentMonths; i++) {
        newPayments.push({
          month: i,
          amount: monthlyPayment,
          label: i === installmentMonths ? "Final Payment" : `Payment ${i}`,
          date: addMonths(startDate, i - 1),
        });
      }
    }

    // Only update if the structure changed, preserve existing dates where possible
    setInstallmentPayments((prev) => {
      // If we have existing payments with the same structure, preserve their dates
      if (prev.length === newPayments.length) {
        return newPayments.map((payment, idx) => ({
          ...payment,
          date: prev[idx]?.date || payment.date,
        }));
      }
      return newPayments;
    });
  }, [
    isInstallment,
    installmentMonths,
    formData.items,
    formData.vatOption,
    formData.vatMethod,
    formData.vatRate,
    formData.date,
  ]);

  // Handler to update individual payment dates
  const handlePaymentDateChange = (index, newDate) => {
    setInstallmentPayments((prev) =>
      prev.map((payment, idx) =>
        idx === index ? { ...payment, date: newDate } : payment
      )
    );
  };

  const handleSendEmail = async ({ subject, body, recipient }) => {
    setLoading(true);
    setLoadingText("Sending. Please wait");

    const emailBody = mailForm(body);

    try {
      const tempType = formType === "noc" ? "noc" : "invoice";
      const attachment = {
        url: formatOldUrl(formData.pdfUrl),
        fileName: `${formData.header}_${formData.invoiceNo}_${tempType}.pdf`,
      };

      const response = await fetch(
        `${process.env.REACT_APP_FIREBASE_BASE_API}/sendEmail`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            data: {
              to: recipient.trim(),
              subject: subject.trim(),
              message: emailBody,
              attachment: attachment,
            },
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Server error. Please try again.");
      }

      const result = await response.json();
      toast.success(result.data?.message || "Email sent successfully!");
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setIsSendModalOpen(false);
      setLoading(false);
      setLoadingText("");
    }
  };

  // transform currentInvoiceClients for React Select
  const clientOptions = useMemo(() => {
    if (!currentInvoiceClients) return [{ value: "", label: "New Client" }];

    return [
      { value: "", label: "New Client" },
      ...currentInvoiceClients.map((client) => ({
        value: client.id,
        label: `${client.name}`,
      })),
    ];
  }, [currentInvoiceClients]);

  const installmentOptions = useMemo(
    () =>
      Array.from({ length: 23 }, (_, i) => {
        const month = i + 2;
        return {
          value: month,
          label: `${month} Months`,
        };
      }),
    []
  );

  const selectedOption = useMemo(() => {
    if (!formData.to.name || formData.to.name.trim() === "") {
      return { value: "", label: "New Client" };
    }

    const foundClient = currentInvoiceClients?.find(
      (client) =>
        client.name.trim().toLowerCase() ===
        formData.to.name.trim().toLowerCase()
    );

    return foundClient
      ? {
          value: foundClient.id,
          label: `${foundClient.name}`,
        }
      : { value: "", label: "New Client" };
  }, [currentInvoiceClients, formData?.to?.name]);

  const handleClientSelect = (selectedOption) => {
    if (!selectedOption || selectedOption.value === "") {
      // Clear all fields when "New Client" is selected
      setFormData((prev) => ({
        ...prev,
        to: {
          name: "",
          tinNo: "",
          address: "",
          contact: "",
          rep: "",
        },
      }));
      return;
    }

    // Find the selected client
    const selectedClient = currentInvoiceClients.find(
      (client) => client.id === selectedOption.value
    );

    if (selectedClient) {
      setFormData((prev) => ({
        ...prev,
        to: {
          name: selectedClient.name || "",
          tinNo: selectedClient.tinNo || "",
          address: selectedClient.address || "",
          contact: selectedClient.contact || "",
          rep: selectedClient.rep || "",
          email: selectedClient.email || "",
        },
      }));
    }
  };

  const handleChange = (e, field, subfield) => {
    if (subfield) {
      setFormData((prev) => ({
        ...prev,
        [field]: { ...prev[field], [subfield]: e.target.value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    }
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedItems = prev.items.map((item, i) => {
        if (i !== index) return item;

        const updatedItem = { ...item };

        if (field === "unitCost" || field === "discount") {
          // Store the raw value as string for display
          updatedItem[field] = value;

          // Use numeric value for calculations
          const numValue =
            value === "" || value === null ? 0 : Number(value) || 0;

          const quantity =
            item.quantity === "" || item.quantity === 0
              ? 1
              : Number(item.quantity) || 1;
          const unitCost =
            field === "unitCost"
              ? numValue
              : item.unitCost === "" || item.unitCost === 0
              ? 0
              : Number(item.unitCost) || 0;
          const discount =
            field === "discount"
              ? numValue
              : item.discount === "" || item.discount === 0
              ? 0
              : Number(item.discount) || 0;

          updatedItem.subTotal = quantity * unitCost - discount;
        } else if (field === "quantity") {
          const numValue =
            value === "" || value === null ? 1 : Number(value) || 1;
          updatedItem.quantity = numValue;

          const unitCost =
            item.unitCost === "" || item.unitCost === 0
              ? 0
              : Number(item.unitCost) || 0;
          const discount =
            item.discount === "" || item.discount === 0
              ? 0
              : Number(item.discount) || 0;

          updatedItem.subTotal = numValue * unitCost - discount;
        } else {
          updatedItem[field] = value;
        }

        return updatedItem;
      });
      return { ...prev, items: updatedItems };
    });
  };
  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: "",
          quantity: 1,
          unitCost: 0,
          discount: 0,
          comment: "",
          subTotal: 0,
        },
      ],
    }));
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  const mapFormTypeToTab = (type) => {
    switch (type) {
      case "noc":
        return "noc";
      default:
        return "invoices";
    }
  };

  // Memoize navigation handler.
  const handleGoBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsConfirmModalOpen(true);
    } else {
      const curTab = mapFormTypeToTab(formType);
      navigate(`/receipts/${curTab}`);
    }
  }, [navigate, hasUnsavedChanges, formType]);

  const confirmNavigation = () => {
    setIsConfirmModalOpen(false);
    const curTab = mapFormTypeToTab(formType);
    navigate(`/receipts/${curTab}`);
  };

  const cancelNavigation = () => {
    setIsConfirmModalOpen(false);
  };

  const handleGenerate = useCallback(
    async (action) => {
      try {
        if (hasUnsavedChanges && action !== "preview") {
          toast.info(
            `To ${action} ${defaultType}, please save your changes first to ensure all modifications are applied correctly.`
          );
          return;
        }

        setLoading(true);
        setLoadingText(
          action === "download"
            ? "Preparing your download. Please wait.."
            : `Preparing ${defaultType}. Please wait..`
        );

        const totals = calculateInvoiceTotals(formData.items || [], {
          vatOption: formData.vatOption,
          vatMethod: formData.vatMethod,
          vatRate: formData.vatRate,
        });

        const formattedTotals = {
          totalSales: formatCurrency(totals.totalSales),
          totalDiscount: formatCurrency(totals.totalDiscount),
          vatableSales: formatCurrency(totals.vatableSales),
          vat: formatCurrency(totals.vat),
          totalPayable: formatCurrency(totals.totalPayable),
        };

        let tempData = {
          ...formData,
          ...formattedTotals,
        };

        tempData.installmentMonths = installmentMonths;
        tempData.isInstallment = isInstallment;
        tempData.installmentPayments = installmentPayments;

        // find unique cid of client using current client data
        const curClient = findExistingClient(
          tempData.to,
          currentInvoiceClients
        );
        const tempCid = curClient?.cid || null;

        const dataBlob = await generatePDF(
          tempData,
          action,
          tempCid,
          formType,
          currentTemplates,
          user.uid,
          privateKeyInMemory
        );

        if (action === "print" && dataBlob) {
          const blobUrl = URL.createObjectURL(dataBlob);
          const iframe = document.createElement("iframe");
          iframe.style.visibility = "hidden";
          iframe.style.position = "fixed";
          iframe.style.right = "0";
          iframe.style.bottom = "0";
          iframe.src = blobUrl;
          document.body.appendChild(iframe);
          iframe.onload = () => {
            iframe.contentWindow?.print();
            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
              document.body.removeChild(iframe);
            }, 60000);
          };
        } else if (action === "preview" && dataBlob) {
          const blobUrl = URL.createObjectURL(dataBlob);

          // Save the URL in formData.pdfUrl
          setLocalPdfUrl(blobUrl);

          // Open preview modal
          setShowPreview(true);
        }
      } catch (error) {
        console.error(error);
        toast.error(`Failed to generate ${defaultType}.`);
      } finally {
        setLoading(false);
        setLoadingText("");
      }
    },
    [
      formData,
      hasUnsavedChanges,
      currentTemplates,
      privateKeyInMemory,
      defaultType,
      currentInvoiceClients,
      formType,
      installmentMonths,
      isInstallment,
      installmentPayments,
      user,
    ]
  );

  const handleNavigate = useCallback(
    (uid) => {
      navigate(`/invoice-form/${uid}`);
    },
    [navigate]
  );

  // Helper function to save new client or update existing one - updated for admin mode
  const saveOrUpdateClient = useCallback(
    async (clientData) => {
      try {
        if (!clientData.name || clientData.name.trim() === "") {
          return null; // Don't save empty clients
        }

        const existingClient = findExistingClient(
          clientData,
          currentInvoiceClients
        );

        const clientToSave = {
          name: clientData.name.trim(),
          address: clientData.address?.trim() || "",
          tinNo: clientData.tinNo?.trim() || "",
          contact: clientData.contact?.trim() || "",
          rep: clientData.rep?.trim() || "",
          email: clientData.email?.trim() || "",
        };

        if (existingClient) {
          // Check if any data has changed
          if (hasClientDataChanged(clientData, existingClient)) {
            // Update existing client (preserve existing CID)
            const targetUserId = isViewingOtherUser
              ? selectedUserId
              : currentUser.uid;
            const clientRef = doc(
              db,
              "users",
              targetUserId,
              "invoiceClients",
              existingClient.id
            );
            await updateDoc(clientRef, {
              ...clientToSave,
              updatedAt: new Date().toISOString(),
            });

            const updatedClient = {
              id: existingClient.id,
              cid: existingClient.cid,
              ...clientToSave,
              updatedAt: new Date().toISOString(),
            };

            // Update the state with the updated client data using appropriate method
            currentUpdateClient(existingClient.id, {
              ...clientToSave,
              updatedAt: new Date().toISOString(),
            });

            toast.info("Client information updated.");
            return updatedClient;
          } else {
            return existingClient;
          }
        } else {
          // Create new client with generated CID
          const newCID = generateNextCID(currentInvoiceClients);
          const targetUserId = isViewingOtherUser
            ? selectedUserId
            : currentUser.uid;

          const newClientData = {
            ...clientToSave,
            cid: newCID,
            createdAt: new Date().toISOString(),
          };

          const clientRef = await addDoc(
            collection(db, "users", targetUserId, "invoiceClients"),
            newClientData
          );

          const savedClient = { id: clientRef.id, ...newClientData };

          // Add the new client to the state using appropriate method
          currentAddClient(savedClient);

          toast.success(`New client added to your database (${newCID}).`);
          return savedClient;
        }
      } catch (error) {
        console.error("Error saving/updating client:", error);
        toast.warn(
          "Invoice saved, but failed to save/update client information."
        );
        return null;
      }
    },
    [
      currentInvoiceClients,
      currentUser,
      currentAddClient,
      currentUpdateClient,
      isViewingOtherUser,
      selectedUserId,
    ]
  );

  //handleSubmit function with client auto-save functionality - updated for admin mode
  const handleSubmit = useCallback(
    async (e) => {
      if (!currentUser) return;
      if (e && e.preventDefault) e.preventDefault();

      // Prevent saving if there are no changes
      if (!hasUnsavedChanges) {
        toast.info("No changes to save.");
        return;
      }

      try {
        setLoading(true);
        setLoadingText("Saving...");

        // Save or update client if needed
        if (formData.to.name && formData.to.name.trim() !== "") {
          setLoadingText("Processing client information...");
          await saveOrUpdateClient(formData.to);
        }

        const targetUserId = isViewingOtherUser
          ? selectedUserId
          : currentUser.uid;
        let updatedFormData = { ...formData, createdBy: targetUserId };

        updatedFormData.invoiceNo = formData.invoiceNo;
        updatedFormData.templateRef = formData.templateRef;
        updatedFormData.isInstallment = isInstallment;
        updatedFormData.installmentMonths = installmentMonths;
        updatedFormData.installmentPayments = installmentPayments;

        const totals = calculateInvoiceTotals(updatedFormData.items || [], {
          vatOption: updatedFormData.vatOption,
          vatMethod: updatedFormData.vatMethod,
          vatRate: updatedFormData.vatRate,
        });

        const formattedTotals = {
          totalSales: formatCurrency(totals.totalSales),
          totalDiscount: formatCurrency(totals.totalDiscount),
          vatableSales: formatCurrency(totals.vatableSales),
          vat: formatCurrency(totals.vat),
          totalPayable: formatCurrency(totals.totalPayable),
        };

        const data = {
          ...updatedFormData,
          ...formattedTotals,
        };

        delete data.gcashQr;
        delete data.gcashQr2;
        delete data.alignedQRs;
        delete data.qrAlignment;
        delete data.cashDetails;
        delete data.checkDetails;

        try {
          const curClient = findExistingClient(data.to, currentInvoiceClients);
          const tempCid = curClient?.cid || null;

          setLoadingText("Preparing PDF...");
          const pdfUrl = await savePdfToCloud(
            data,
            targetUserId,
            tempCid,
            formType,
            currentTemplates,
            privateKeyInMemory
          );
          updatedFormData.pdfUrl = pdfUrl;
        } catch (error) {
          console.error(`Failed to save invoice PDF:`, error);
        }

        // console.log("data to be saved: ", updatedFormData);
        let encryptedInvoice = updatedFormData;
        try {
          setLoadingText(`Encrypting ${defaultType}...`);
          let recipients = isViewingOtherUser
            ? await getRecipientById(targetUserId)
            : await getRecipients();

          encryptedInvoice = await encryptInvoice(updatedFormData, recipients);
        } catch (error) {
          console.error(error);
        }

        setLoadingText(`Saving ${defaultType}...`);
        let docRef;
        if (isNew) {
          docRef = await addDoc(collection(db, "invoices"), encryptedInvoice);
          toast.success(`${defaultType} created successfully!`);
        } else {
          docRef = doc(db, "invoices", id);
          await updateDoc(docRef, encryptedInvoice);
          toast.success(`${defaultType} updated successfully!`);
        }

        if (isNew && updatedFormData.templateRef) {
          await updateLastInvoiceNumber(updatedFormData.templateRef);
        }

        const stateData = { ...updatedFormData, id: isNew ? docRef.id : id };
        if (isViewingOtherUser) {
          if (isNew) {
            addUserInvoice(stateData);
          } else {
            updateUserInvoice(id, updatedFormData);
          }
        } else {
          setInvoices((prevInvoices) => [
            ...(isNew
              ? [...prevInvoices, stateData]
              : prevInvoices.map((invoice) =>
                  invoice.id === id ? { id, ...updatedFormData } : invoice
                )),
          ]);
        }

        setOriginalData(updatedFormData);
        setFormData(updatedFormData);

        if (isNew) {
          const invoiceId = docRef.id;
          handleNavigate(invoiceId);
        }
      } catch (error) {
        console.error(`Error saving ${defaultType}:`, error);
        toast.error(`Failed to save ${defaultType}. Please try again.`);
      } finally {
        setLoading(false);
      }
    },
    [
      formData,
      currentUser,
      isNew,
      id,
      privateKeyInMemory,
      getRecipients,
      setInvoices,
      addUserInvoice,
      updateUserInvoice,
      hasUnsavedChanges,
      handleNavigate,
      currentTemplates,
      defaultType,
      saveOrUpdateClient,
      formType,
      currentInvoiceClients,
      isViewingOtherUser,
      selectedUserId,
      installmentMonths,
      isInstallment,
      installmentPayments,
    ]
  );

  const newInvoiceHandler = useCallback(async () => {
    if (hasUnsavedChanges) {
      toast.info(
        "Please save your changes before creating a new one to ensure all modifications are applied correctly"
      );
      return;
    }

    let lastInvoiceNumber = 0;

    if (formData.templateRef) {
      const fetchedInvoiceNumber = await getLastInvoiceNumber(
        formData.templateRef
      );
      if (fetchedInvoiceNumber !== null && fetchedInvoiceNumber !== undefined) {
        lastInvoiceNumber = fetchedInvoiceNumber;
        toast.info(
          `New ${defaultType} ${formatInvoiceNumber(
            lastInvoiceNumber,
            formType
          )} is loaded!`
        );
      } else {
        toast.info(
          `Unable to locate the ${defaultType} template. The system will assign  #1 as the starting number.`
        );
        lastInvoiceNumber = 0;
      }
    } else {
      toast.info(
        `Unable to locate the ${defaultType} template. The system will assign  #1 as the starting number.`
      );
      lastInvoiceNumber = 0;
    }

    const tempData = {
      header: formData.header,
      headerDescription: formData.headerDescription || "",
      lastInvoiceNumber: Number(lastInvoiceNumber),
      logo: formData.logo || null,
      date: defaultData.date,
      offerExpiry: defaultData.offerExpiry,
      from: { ...formData.from },
      items: [
        {
          description: "",
          quantity: 1,
          unitCost: "",
          discount: "",
          comment: "",
          subTotal: 0,
        },
      ],
      pdfUrl: null,
      templateRef: formData.templateRef,
      vatOption: formData.vatOption || "vat",
      vatMethod: formData.vatMethod || "inclusive",
      taxCode: formData.taxCode || "",
      vatRate: formData.vatRate || 12,
    };

    navigate("/invoice-form", { state: { template: tempData } });
  }, [
    hasUnsavedChanges,
    formData,
    navigate,
    defaultData,
    defaultType,
    formType,
  ]);

  const handleHeaderChange = (e) => {
    const newHeader = e.target.value;
    setHeader(newHeader);
    setFormData((prev) => ({ ...prev, header: newHeader }));
  };

  // Filter invoices to exclude those with invoiceNo starting with "TL" or "AR"
  const filteredInvoices = useMemo(() => {
    if (!currentInvoices || currentInvoices.length === 0) return [];

    return currentInvoices.filter((invoice) => {
      const no = invoice.invoiceNo || "";
      if (!no) return false;

      if (formType === "noc") {
        return no.startsWith("NC");
      }

      if (formType === "transmittal") {
        return no.startsWith("TL");
      }

      if (formType === "acknowledgement") {
        return no.startsWith("AR");
      }

      if (formType === "sec-res") {
        return no.startsWith("SR");
      }

      if (formType === "legal-advice") {
        return no.startsWith("LA");
      }

      if (formType === "invoices") {
        // Regular invoices - exclude all special types
        return (
          !no.startsWith("NC") &&
          !no.startsWith("TL") &&
          !no.startsWith("AR") &&
          !no.startsWith("SR") &&
          !no.startsWith("LA")
        );
      }

      // Default fallback - exclude all special types
      return (
        !no.startsWith("TL") &&
        !no.startsWith("AR") &&
        !no.startsWith("NC") &&
        !no.startsWith("SR") &&
        !no.startsWith("LA")
      );
    });
  }, [currentInvoices, formType]);

  // Find the index of the current invoice in the filtered list
  const currentInvoiceIndex = useMemo(() => {
    if (!id || filteredInvoices.length === 0) return -1;
    return filteredInvoices.findIndex((invoice) => invoice.id === id);
  }, [id, filteredInvoices]);

  // Navigation handlers using the filteredInvoices array
  const handleNextInvoice = () => {
    if (
      currentInvoiceIndex !== -1 &&
      currentInvoiceIndex < filteredInvoices.length - 1
    ) {
      // Navigate to the next invoice
      const nextInvoiceId = filteredInvoices[currentInvoiceIndex + 1].id;
      navigate(`/invoice-form/${nextInvoiceId}`);
    }
  };

  const handlePreviousInvoice = () => {
    if (currentInvoiceIndex > 0) {
      // Navigate to the previous invoice
      const previousInvoiceId = filteredInvoices[currentInvoiceIndex - 1].id;
      navigate(`/invoice-form/${previousInvoiceId}`);
    }
  };

  // --- Memoize Button Config ---
  const buttonConfig = useMemo(() => {
    const cfg = [
      {
        onClick: handleGoBack,
        icon: "arrow_back",
        label: "Go back",
        className: "text-xs",
      },
      ...(!isNew
        ? [
            {
              onClick: newInvoiceHandler,
              icon: "note_add",
              label: `New ${defaultType}`,
              className: "text-xs",
            },
          ]
        : []),
      {
        onClick: () => handleGenerate("preview"),
        icon: "visibility",
        label: `Preview ${defaultType}`,
        className: "text-xs",
      },
      {
        onClick: () => handleGenerate("print"),
        icon: "print",
        label: "Print",
        className: "text-xs",
      },
      {
        onClick: () => handleGenerate("download"),
        icon: "cloud_download",
        label: "Download",
        className: "text-xs",
      },
      {
        onClick: () => {
          if (hasUnsavedChanges) {
            toast.info(
              `Please save your changes before sending the ${defaultType} to ensure all modifications are applied correctly`
            );
            return;
          }
          setIsSendModalOpen(true);
        },
        icon: "email",
        label: "Send Email",
        className: "text-xs",
      },
      {
        onClick: () => handleSubmit(),
        icon: "save",
        label: `Save ${defaultType}`,
        className: "text-xs",
      },
    ];
    return cfg;
  }, [
    handleGoBack,
    isNew,
    newInvoiceHandler,
    setShowPreview,
    handleGenerate,
    hasUnsavedChanges,
    setIsSendModalOpen,
    handleSubmit,
    defaultType,
  ]);

  useEffect(() => {
    setNavbarButtons(buttonConfig);
    return () => setNavbarButtons([]);
  }, [setNavbarButtons, buttonConfig]);

  return (
    <div className="bg-inherit">
      <div className="p-6 flex justify-center">
        {!isNew && (
          <div className="flex flex-col fixed top-42 right-0 py-4 px-2 space-y-3 z-20 bg-tier-primary dark:bg-tier-primary-dark dark:border-gray-600 border bg-opacity-70 shadow-lg rounded-l-lg">
            {/* Back */}
            <div className="relative group">
              <button
                onClick={handlePreviousInvoice}
                className="btn-floating focus:ring-opacity-50 "
              >
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <span className="whitespace-nowrap absolute right-full top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 text-color bg-gray-600 p-2 rounded text-sm transition-opacity duration-300">
                Previous
              </span>
            </div>

            <div className="relative group border-y">
              <div className="text-sm">
                {currentInvoiceIndex !== -1 ? currentInvoiceIndex + 1 : 0} of{" "}
                {filteredInvoices.length}
              </div>
            </div>

            {/* Next */}
            <div className="relative group">
              <button
                onClick={handleNextInvoice}
                className="btn-floating focus:ring-opacity-50 "
              >
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
              <span className="whitespace-nowrap absolute right-full top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 text-color bg-gray-600 p-2 rounded text-sm transition-opacity duration-300">
                Next
              </span>
            </div>
          </div>
        )}

        {loading && <Loader text={loadingText} />}

        {isSendModalOpen && (
          <SendInvoiceModal
            isOpen={isSendModalOpen}
            onClose={() => setIsSendModalOpen(false)}
            onSend={handleSendEmail}
            invoice={formData}
            defaultSubject={`${formData.from.name} - ${formData.header} [ #${formData.invoiceNo} ]`}
          />
        )}

        {/* Confirm Navigation Modal */}
        <ConfirmNavigationModal
          isOpen={isConfirmModalOpen}
          onCancel={cancelNavigation}
          onConfirm={confirmNavigation}
          message="You have unsaved changes. Are you sure you want to leave without saving?"
        />

        {showPreview && (
          <PdfPreviewModal
            invoiceData={formData}
            pdfUrl={localPdfUrl}
            onClose={() => setShowPreview(false)}
          />
        )}

        <div className="flex flex-col gap-2 w-full max-w-6xl min-w-[768px]">
          {/* Show indicator when viewing another user's data */}
          {isViewingOtherUser && (
            <div
              className={`mb-4 p-3 border-l-4 rounded-r-md ${
                isViewingOtherUser
                  ? "bg-green-100 dark:bg-green-900 border-green-500 text-green-700 dark:text-green-200"
                  : "bg-teal-100 dark:bg-teal-900 border-teal-500 text-teal-700 dark:text-teal-200"
              }`}
            >
              <div className="flex items-center">
                <span className="material-symbols-outlined mr-2">
                  {isViewingOtherUser ? "admin_panel_settings" : "visibility"}
                </span>
                <div>
                  <p>
                    <span className="font-medium">
                      {isViewingOtherUser
                        ? "Admin Mode: Managing "
                        : "Viewing "}
                    </span>
                    <span className="font-bold text-lg dark:text-tier-secondary text-amber-600">
                      {getSelectedUserName()}
                    </span>
                    <span className="font-medium">'s invoice</span>
                  </p>
                  <p className="text-sm mt-1 opacity-90">
                    {isViewingOtherUser
                      ? "Full admin access: You can view, create, edit, delete, download, and email."
                      : "Read-only mode: You can view, download, and email, but cannot create, edit, or delete."}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="form-section max-w-6xl w-full">
            <div className="flex items-center font-bold text-sm text-teal-400 space-x-2">
              <span className="material-symbols-outlined text-sm">circle</span>
              <span>
                {formData.vatOption === "vat" || formData.vatOption === "tax"
                  ? formData.vatMethod === "inclusive"
                    ? `${formData.vatOption.toUpperCase()} Inclusive`
                    : `${formData.vatOption.toUpperCase()} Exclusive`
                  : "Non-VAT"}
              </span>

              {!showVatOptions && (
                <button
                  className="underline font-normal text-tier-secondary hover:text-dark-alt"
                  onClick={() => setShowVatOptions(true)}
                >
                  Change
                </button>
              )}
            </div>

            {showVatOptions && (
              <div className="m-4 text-color bg-white/10 p-6 rounded-lg">
                <h2 className="text-xl font-bold mb-4">Tax Options</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label htmlFor="vatOption" className="block text-xs">
                      Invoice Type
                    </label>

                    <Select
                      id="vatOption"
                      value={vatOptions.find(
                        (opt) => opt.value === formData.vatOption
                      )}
                      onChange={(selected) =>
                        setFormData((prev) => ({
                          ...prev,
                          vatOption: selected.value,
                        }))
                      }
                      options={vatOptions}
                      styles={selectStyles}
                      classNamePrefix="react-select"
                    />
                  </div>
                  {formData.vatOption !== "non-vat" && (
                    <>
                      <div>
                        <label htmlFor="vatMethod" className="block text-xs">
                          {formData.vatOption === "tax" ? "Tax" : "VAT"} Type
                        </label>

                        <Select
                          id="vatMethod"
                          value={vatMethodOptions.find(
                            (opt) => opt.value === formData.vatMethod
                          )}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              vatMethod: selected.value,
                            }))
                          }
                          options={vatMethodOptions}
                          styles={selectStyles}
                          classNamePrefix="react-select"
                        />
                      </div>
                    </>
                  )}
                  {formData.vatOption === "tax" && (
                    <>
                      <div>
                        <label htmlFor="taxCode" className="block text-xs">
                          Tax Code
                        </label>
                        <input
                          id="taxCode"
                          type="text"
                          value={formData.taxCode}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              taxCode: e.target.value,
                            }))
                          }
                          className="custom-input"
                        />
                      </div>
                    </>
                  )}
                  {formData.vatOption !== "non-vat" && (
                    <div>
                      <label htmlFor="vatRate" className="block text-xs">
                        {formData.vatOption === "tax" ? "Tax" : "VAT"} Rate (%)
                      </label>
                      <input
                        id="vatRate"
                        type="number"
                        value={formData.vatRate}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            vatRate: e.target.value,
                          }))
                        }
                        className="custom-input"
                      />
                    </div>
                  )}
                </div>
                <button
                  className="mt-4 btn-action"
                  onClick={() => setShowVatOptions(false)}
                >
                  Close
                </button>
              </div>
            )}

            <div className="border-b-2 border-tier-secondary my-4"></div>

            <form onSubmit={handleSubmit}>
              {/* Header and Logo on same row */}
              <div className="flex items-center justify-between gap-4 m-4">
                <div className="w-full space-y-2">
                  <div className="flex flex-col w-full text-center">
                    <label htmlFor="header" className="block text-xs">
                      Document Title
                    </label>
                    {/* Header Input */}
                    <input
                      id="header"
                      type="text"
                      value={header}
                      onChange={handleHeaderChange}
                      className="text-2xl px-4 py-2 font-bold text-color bg-white/20 rounded-lg text-center w-full focus:outline-none"
                      placeholder="Enter Header"
                    />
                  </div>
                  <div className="flex flex-col w-full text-center">
                    <label
                      htmlFor="header-description"
                      className="block text-xs"
                    >
                      Description / Subtitle
                    </label>
                    {/* Header Input */}
                    <input
                      id="header-description"
                      type="text"
                      value={formData.headerDescription}
                      onChange={(e) => handleChange(e, "headerDescription")}
                      className="text-xl px-4 py-2 font-bold text-color bg-white/20 rounded-lg text-center w-full focus:outline-none"
                      placeholder="Enter description / subtitle"
                    />
                  </div>
                </div>

                {/* Logo */}
                {formData.logo && (
                  <div className="flex flex-col items-center gap-2">
                    <span className="block text-xs">Header Image</span>
                    <img
                      src={formatOldUrl(formData.logo)}
                      alt="Company Logo Preview"
                      className="h-28 min-w-32 w-auto object-contain border dark:border-gray-600 rounded"
                    />
                  </div>
                )}
              </div>

              <div className="border-b-2 border-tier-secondary pb-4"></div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 m-4">
                <div>
                  <label htmlFor="date" className="block text-sm">
                    Date
                  </label>
                  <input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => {
                      handleChange(e, "date");
                      const newOfferExpiry = calculateOfferExpiry(
                        e.target.value
                      );
                      setFormData((prev) => ({
                        ...prev,
                        date: e.target.value,
                        offerExpiry: newOfferExpiry,
                      }));
                    }}
                    className="block custom-input"
                  />
                </div>
                <div>
                  <label htmlFor="invoiceNo" className="block text-sm">
                    Invoice No
                  </label>
                  <input
                    id="invoiceNo"
                    type="text"
                    value={formData.invoiceNo}
                    onChange={(e) => handleChange(e, "invoiceNo")}
                    className="block custom-input"
                  />
                </div>
                <div>
                  <label htmlFor="offerExpiry" className="block text-sm">
                    Offer Expiry
                  </label>
                  <input
                    id="offerExpiry"
                    type="date"
                    value={formData.offerExpiry || ""}
                    onChange={(e) => handleChange(e, "offerExpiry")}
                    className="block custom-input"
                  />
                </div>
              </div>

              <div className="border-b-2 border-tier-secondary pb-4"></div>

              <div className="m-4">
                <h2 className="text-xl font-bold mb-3">Issuing Company</h2>
                <label htmlFor="from-name" className="block text-sm">
                  Name
                </label>
                <input
                  id="from-name"
                  type="text"
                  value={formData.from.name || ""}
                  onChange={(e) => handleChange(e, "from", "name")}
                  className="block custom-input mb-3"
                />
                <label htmlFor="from-address" className="block text-sm">
                  Address
                </label>
                <input
                  id="from-address"
                  type="text"
                  value={formData.from.address || ""}
                  onChange={(e) => handleChange(e, "from", "address")}
                  className="block custom-input mb-3"
                />
                <label htmlFor="from-contact" className="block text-sm">
                  Contact
                </label>
                <input
                  id="from-contact"
                  type="text"
                  value={formData.from.contact || ""}
                  onChange={(e) => handleChange(e, "from", "contact")}
                  className="block custom-input"
                />
              </div>

              <div className="border-b-2 border-tier-secondary pb-4"></div>

              <div className="m-4">
                <h2 className="text-xl font-bold mb-4">
                  {formType === "inv" ? "Bill" : "Charge"} To
                </h2>

                {/* Client Selection Dropdown */}
                <div className="mb-4">
                  <label htmlFor="client-select" className="block text-sm mb-2">
                    Select Client
                  </label>

                  <Select
                    value={selectedOption}
                    onChange={handleClientSelect}
                    options={clientOptions}
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    isSearchable={true}
                    placeholder="Select client..."
                    noOptionsMessage={() => "No clients found"}
                    loadingMessage={() => "Loading clients..."}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="to-name" className="block text-sm">
                      Name
                    </label>
                    <input
                      id="to-name"
                      type="text"
                      value={formData.to.name || ""}
                      onChange={(e) => handleChange(e, "to", "name")}
                      className="w-full custom-input"
                      placeholder="Enter name"
                    />
                  </div>

                  <div>
                    <label htmlFor="to-tinNo" className="block text-sm">
                      TIN
                    </label>
                    <input
                      id="to-tinNo"
                      type="text"
                      value={formData.to.tinNo || ""}
                      onChange={(e) => handleChange(e, "to", "tinNo")}
                      className="w-full custom-input"
                      placeholder="Enter TIN"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="to-representative"
                      className="block text-sm"
                    >
                      Representative
                    </label>
                    <input
                      id="to-representative"
                      type="text"
                      value={formData.to.rep || ""}
                      onChange={(e) => handleChange(e, "to", "rep")}
                      className="w-full custom-input"
                      placeholder="Enter representative name"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="to-address" className="block text-sm">
                      Address
                    </label>
                    <input
                      id="to-address"
                      type="text"
                      value={formData.to.address || ""}
                      onChange={(e) => handleChange(e, "to", "address")}
                      className="w-full custom-input"
                      placeholder="Enter address"
                    />
                  </div>

                  <div>
                    <label htmlFor="to-contact" className="block text-sm">
                      Contact
                    </label>
                    <input
                      id="to-contact"
                      type="text"
                      value={formData.to.contact || ""}
                      onChange={(e) => handleChange(e, "to", "contact")}
                      className="w-full custom-input"
                      placeholder="Enter contact details"
                    />
                  </div>

                  <div>
                    <label htmlFor="to-email" className="block text-sm">
                      Email
                    </label>
                    <input
                      id="to-email"
                      type="text"
                      value={formData.to.email || ""}
                      onChange={(e) => handleChange(e, "to", "email")}
                      className="w-full custom-input"
                      placeholder="Enter email address"
                    />
                  </div>
                </div>
              </div>

              <div className="border-b-2 border-tier-secondary pb-4"></div>

              <div className="m-4">
                <div className="flex items-center gap-4 mb-4">
                  <h2 className="text-xl font-bold">Payment Terms</h2>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isInstallment}
                      onChange={(e) => {
                        setIsInstallment(e.target.checked);
                        if (!e.target.checked) {
                          setInstallmentMonths(2);
                          setInstallmentPayments([]);
                        }
                      }}
                      className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm">Enable Installment Payment</span>
                  </label>
                </div>

                {isInstallment && (
                  <div className="bg-white/10 p-4 rounded-lg space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="installment-months"
                          className="block text-sm mb-2"
                        >
                          Number of Months
                        </label>

                        <Select
                          id="installment-months"
                          value={installmentOptions.find(
                            (opt) => opt.value === installmentMonths
                          )}
                          onChange={(selectedOption) => {
                            setInstallmentMonths(selectedOption?.value);
                          }}
                          options={installmentOptions}
                          styles={selectStyles}
                          classNamePrefix="react-select"
                          isSearchable={true}
                          placeholder="Select number of months..."
                          noOptionsMessage={() => ""}
                          loadingMessage={() => "Loading months..."}
                        />
                      </div>

                      <div className="flex items-end">
                        <div className="w-full">
                          <label className="block text-sm mb-2">
                            Payment Structure
                          </label>
                          <div className="text-xs text-tier-secondary">
                            {installmentMonths === 2
                              ? "2 equal payments"
                              : `50% downpayment + ${
                                  installmentMonths - 1
                                } equal payments`}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Payment Breakdown - Now Editable */}
                    {installmentPayments.length > 0 && (
                      <div className="mt-4">
                        <h3 className="text-sm font-semibold mb-3 text-tier-secondary">
                          Payment Breakdown (Editable)
                        </h3>
                        <div className="bg-white/5 rounded-lg overflow-hidden border border-alt-color">
                          <table className="w-full text-sm">
                            <thead className="bg-tier-secondary text-tier-primary">
                              <tr>
                                <th className="px-4 py-2 text-left">
                                  Description
                                </th>
                                <th className="px-4 py-2 text-left">Date</th>
                                <th className="px-4 py-2 text-right">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-alt-color">
                              {installmentPayments.map((payment, idx) => (
                                <tr key={idx} className="hover:bg-white/5">
                                  <td className="px-4 py-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-semibold text-tier-secondary">
                                        Term {payment.month}:
                                      </span>
                                      <span>{payment.label}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2">
                                    <input
                                      type="date"
                                      value={payment.date || ""}
                                      onChange={(e) =>
                                        handlePaymentDateChange(
                                          idx,
                                          e.target.value
                                        )
                                      }
                                      className="custom-input text-sm py-1 px-2"
                                    />
                                  </td>
                                  <td className="px-4 py-2 text-right font-mono">
                                    {formatCurrency(payment.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-tier-secondary/50">
                              <tr>
                                <td
                                  colSpan="2"
                                  className="px-4 py-2 text-right font-semibold"
                                >
                                  Total:
                                </td>
                                <td className="px-4 py-2 text-right font-semibold font-mono">
                                  {formatCurrency(
                                    installmentPayments.reduce(
                                      (sum, p) => sum + p.amount,
                                      0
                                    )
                                  )}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border-b-2 border-tier-secondary pb-4"></div>

              {/* Items Table */}
              <div className="m-4">
                <h2 className="text-xl font-bold mb-4">Items</h2>
                <div className="mt-2 bg-white/10 rounded-lg shadow-sm border border-alt-color dark:border-gray-600 overflow-hidden">
                  <div className="-m-1.5 overflow-x-auto">
                    <div className="p-1.5 min-w-full inline-block align-start">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-alt-color">
                          <thead className="bg-tier-secondary text-tier-primary">
                            <tr className="text-xs uppercase font-semibold tracking-wide">
                              <th className="table-custom-th-form w-10">
                                Item No.
                              </th>
                              <th className="table-custom-th-form w-1/3">
                                Description
                              </th>
                              <th className="table-custom-th-form w-10">
                                Quantity
                              </th>
                              <th className="table-custom-th-form w-1/5">
                                Unit Cost
                              </th>
                              <th className="table-custom-th-form w-1/5">
                                Discount
                              </th>
                              <th className="table-custom-th-form w-1/5">
                                Subtotal
                              </th>

                              <th className="table-custom-th-form w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.items.map((item, index) => (
                              <React.Fragment key={index}>
                                {/* Main row */}
                                <tr
                                  onMouseEnter={() => setHoveredIndex(index)}
                                  onMouseLeave={() => setHoveredIndex(null)}
                                  className={`text-alt-color ${
                                    hoveredIndex === index
                                      ? "bg-white/5"
                                      : "bg-inherit"
                                  }`}
                                >
                                  <td className="table-custom-td-form">
                                    {index + 1}
                                  </td>
                                  <td className="table-custom-td-form">
                                    <textarea
                                      id={`description-${index}`}
                                      value={item.description || ""}
                                      onChange={(e) => {
                                        handleItemChange(
                                          index,
                                          "description",
                                          e.target.value
                                        );
                                        e.target.style.height = "auto";
                                        e.target.style.height = `${e.target.scrollHeight}px`;
                                      }}
                                      placeholder="Description"
                                      className="custom-input w-full overflow-hidden resize-none"
                                    />
                                  </td>
                                  <td className="table-custom-td-form">
                                    <input
                                      id={`quantity-${index}`}
                                      type="number"
                                      value={item.quantity ?? 1}
                                      onChange={(e) => {
                                        const value =
                                          e.target.value === ""
                                            ? 1
                                            : Number(e.target.value);
                                        handleItemChange(
                                          index,
                                          "quantity",
                                          value
                                        );
                                      }}
                                      placeholder="Quantity"
                                      className="custom-input"
                                    />
                                  </td>
                                  <td className="table-custom-td-form">
                                    <div className="currency-input-wrapper">
                                      <span className="currency-symbol">₱</span>
                                      <input
                                        id={`unitCost-${index}`}
                                        type="number"
                                        value={
                                          item.unitCost === 0
                                            ? ""
                                            : item.unitCost
                                        }
                                        onChange={(e) => {
                                          handleItemChange(
                                            index,
                                            "unitCost",
                                            e.target.value
                                          );
                                        }}
                                        onFocus={(e) => {
                                          if (e.target.value === "0") {
                                            handleItemChange(
                                              index,
                                              "unitCost",
                                              ""
                                            );
                                          }
                                        }}
                                        placeholder="0"
                                        className="currency-input"
                                      />
                                    </div>
                                  </td>
                                  <td className="table-custom-td-form">
                                    <div className="currency-input-wrapper">
                                      <span className="currency-symbol">₱</span>
                                      <input
                                        id={`discount-${index}`}
                                        type="number"
                                        value={
                                          item.discount === 0
                                            ? ""
                                            : item.discount
                                        }
                                        onChange={(e) => {
                                          handleItemChange(
                                            index,
                                            "discount",
                                            e.target.value
                                          );
                                        }}
                                        onFocus={(e) => {
                                          if (e.target.value === "0") {
                                            handleItemChange(
                                              index,
                                              "discount",
                                              ""
                                            );
                                          }
                                        }}
                                        placeholder="0"
                                        className="currency-input"
                                      />
                                    </div>
                                  </td>
                                  <td className="table-custom-td-form">
                                    <div className="currency-input-wrapper">
                                      <span className="currency-symbol">₱</span>
                                      <input
                                        id={`subTotal-${index}`}
                                        type="number"
                                        value={item.subTotal ?? 0}
                                        disabled
                                        className="currency-input cursor-not-allowed"
                                      />
                                    </div>
                                  </td>
                                  <td className="table-custom-td-form">
                                    <button
                                      type="button"
                                      className="action-btn-tb action-btn-delete"
                                      onClick={() => removeItem(index)}
                                    >
                                      <span className="material-symbols-outlined text-sm">
                                        delete
                                      </span>
                                    </button>
                                  </td>
                                </tr>
                                {/* Comment row */}
                                <tr
                                  onMouseEnter={() => setHoveredIndex(index)}
                                  onMouseLeave={() => setHoveredIndex(null)}
                                  className={`text-tier-primary border-b border-gray-300 ${
                                    hoveredIndex === index
                                      ? "bg-white/5"
                                      : "bg-inherit"
                                  }`}
                                >
                                  <td colSpan={1}></td>
                                  <td
                                    colSpan={"6"}
                                    className="table-custom-td-form"
                                  >
                                    <textarea
                                      id={`comment-${index}`}
                                      value={item.comment || ""}
                                      onChange={(e) => {
                                        handleItemChange(
                                          index,
                                          "comment",
                                          e.target.value
                                        );
                                        e.target.style.height = "auto";
                                        e.target.style.height = `${e.target.scrollHeight}px`;
                                      }}
                                      placeholder="Comment"
                                      className="custom-input text-xs italic w-full overflow-hidden resize-none"
                                    />
                                  </td>
                                  <td colSpan={1}></td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={addItem}
                  className="m-2 btn-action"
                >
                  Add Item
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceForm;
